import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import type { Theme } from '@mui/material';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';

import { useResizablePanels } from '../../../../hooks/useResizablePanels';
import type { LayoutKey } from '../../../../storage/deviceStorage';

import { RESIZER } from './constants';

type Direction = 'horizontal' | 'vertical';

interface ResizablePanelsProps {
    primary: React.ReactNode;
    secondary: React.ReactNode;
    direction?: Direction;
    /** Where this device remembers the split. */
    layoutKey: LayoutKey;
}

const MIN_PANEL_PERCENT = 25;

const baseContainerStyles = {
    display: 'flex',
    height: '100%',
    width: '100%',
};

const dividerBaseStyles = {
    flexShrink: 0,
    flexGrow: 0,
    position: 'relative',
    transition: (theme: Theme) => theme.transitions.create('background-color', { duration: theme.transitions.duration.shorter }),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // A finger dragging the separator moves the split; it never scrolls the page instead.
    touchAction: 'none',
};

const panelBaseStyles = {
    flexShrink: 0,
    flexGrow: 0,
};

export const ResizablePanels: React.FC<ResizablePanelsProps> = ({
    primary,
    secondary,
    direction = 'vertical',
    layoutKey,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { containerRef, panelSize, minSize, maxSize, isDragging, separatorHandlers } = useResizablePanels({
        minSize: MIN_PANEL_PERCENT,
        direction,
        layoutKey,
    });

    if (isMobile) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    gap: 2,
                }}
            >
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>{primary}</Box>
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>{secondary}</Box>
            </Box>
        );
    }

    const isHorizontal = direction === 'horizontal';

    const containerSx = {
        ...baseContainerStyles,
        flexDirection: isHorizontal ? 'row' : 'column',
        userSelect: isDragging ? 'none' : 'auto',
    };

    const primaryPanelSx = {
        ...panelBaseStyles,
        flexBasis: `${panelSize}%`,
        minWidth: isHorizontal ? `${MIN_PANEL_PERCENT}%` : 'auto',
        minHeight: !isHorizontal ? `${MIN_PANEL_PERCENT}%` : 'auto',
    };

    const secondaryPanelSx = {
        flex: 1,
        overflow: 'hidden',
        minWidth: isHorizontal ? `${MIN_PANEL_PERCENT}%` : 'auto',
        minHeight: !isHorizontal ? `${MIN_PANEL_PERCENT}%` : 'auto',
    };

    const dividerSx = {
        ...dividerBaseStyles,
        cursor: isHorizontal ? 'col-resize' : 'row-resize',
        backgroundColor: isDragging ? 'primary.main' : 'transparent',
        width: isHorizontal ? `${RESIZER.thickness}px` : '100%',
        height: isHorizontal ? '100%' : `${RESIZER.thickness}px`,
        mx: `${RESIZER.gutter}px`,
        '&:hover, &:focus-visible': {
            backgroundColor: 'primary.main',
            outline: 'none',
        },
        '&:hover .drag-indicator, &:focus-visible .drag-indicator': {
            color: 'common.white',
        },
    };

    const iconSx = {
        color: isDragging ? 'common.white' : 'text.secondary',
        transform: isHorizontal ? 'none' : 'rotate(90deg)',
        transition: (iconTheme: Theme) => iconTheme.transitions.create('color', { duration: iconTheme.transitions.duration.shorter }),
        fontSize: `${RESIZER.iconSize}px`,
    };

    return (
        <Box boxSizing='border-box' ref={containerRef} sx={containerSx}>
            <Box height='calc(100% - 0rem)' sx={primaryPanelSx}>{primary}</Box>

            <Tooltip
                title={t('common.resizablePanels.dragOrKeys', 'Drag, or use the arrow keys, to resize')}
                placement={isHorizontal ? 'right' : 'bottom'}
                arrow
            >
                <Box
                    role="separator"
                    tabIndex={0}
                    aria-label={t('common.resizablePanels.resize', 'Resize panels')}
                    aria-orientation={isHorizontal ? 'vertical' : 'horizontal'}
                    aria-valuenow={Math.round(panelSize)}
                    aria-valuemin={minSize}
                    aria-valuemax={maxSize}
                    {...separatorHandlers}
                    sx={dividerSx}
                >
                    <DragIndicatorIcon className="drag-indicator" sx={iconSx} />
                </Box>
            </Tooltip>

            <Box height='calc(100% - 0rem)' sx={secondaryPanelSx}>{secondary}</Box>
        </Box>
    );
};
